// src/Customer/pages/AccountDashboard.tsx
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Gift, Compass, Smartphone, CreditCard, Receipt, Wallet, Eye, EyeOff, RefreshCcw, ChevronLeft, ChevronRight, User } from 'lucide-react';

const AccountDashboard = ({ userData }: { userData?: any }) => {
  // Balance Hide/Show State
  const [showBalance, setShowBalance] = useState(false);
    const navigate = useNavigate();
  
  // Ref for the scroll container
  const scrollRef = useRef<HTMLDivElement>(null);

  if (!userData) return null;

  // Format balance to 2 decimal places
  const balance = userData.wallet_balance ? parseFloat(userData.wallet_balance).toFixed(2) : '0.00';

  const quickActions = [
    { name: 'Security', icon: ShieldCheck, color: 'text-green-500', bg: 'bg-green-500/10' },
    { name: 'Explore', icon: Compass, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { name: 'Offers', icon: Gift, color: 'text-purple-500', bg: 'bg-purple-500/10' },
    { name: 'Scan QR', icon: Smartphone, color: 'text-pink-500', bg: 'bg-pink-500/10' },
  ];

  // Smooth Scroll function
  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const scrollTo = direction === 'left' ? scrollLeft - 300 : scrollLeft + 300; // Scroll amount

      scrollRef.current.scrollTo({
        left: scrollTo,
        behavior: 'smooth'
      });
    }
  };



  return (
    <div className="max-w-5xl mx-auto space-y-8">
      
      {/* Desktop Greeting */}
      <div className="hidden md:block">
        <h1 className="text-3xl font-bold text-white">Hello, {userData.first_name}!</h1>
        <p className="text-gray-400 mt-1">Welcome back to your trading dashboard.</p>
      </div>

      {/* Quick Action Circles */}
      <div className="flex justify-between md:justify-start md:gap-8">
        {quickActions.map((action, idx) => (
          <div key={idx} className="flex flex-col items-center gap-2 cursor-pointer group">
            <div className={`w-14 h-14 md:w-16 md:h-16 rounded-full ${action.bg} flex items-center justify-center border border-transparent group-hover:border-gray-600 transition-all`}>
              <action.icon className={action.color} size={24} />
            </div>
            <span className="text-[11px] md:text-xs font-medium text-gray-400 group-hover:text-white transition-colors text-center">{action.name}</span>
          </div>
        ))}
      </div>

      {/* Horizontal Scrolling Cards Section */}
      <div className="relative pt-2">
        
        {/* Navigation Arrows (Hidden on Mobile, Visible on Desktop) */}
        <button 
          onClick={() => scroll('left')} 
          className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-20 items-center justify-center p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all opacity-50 hover:opacity-100"
        >
          <ChevronLeft size={24} />
        </button>
        <button 
          onClick={() => scroll('right')} 
          className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-20 items-center justify-center p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all opacity-50 hover:opacity-100"
        >
          <ChevronRight size={24} />
        </button>

        {/* Scroll Container */}
        <div 
          ref={scrollRef}
          className="flex gap-4 md:gap-6 overflow-x-auto md:overflow-x-hidden pb-4 snap-x pt-2 custom-scrollbar"
        >
          
          {/* Card 1: Main Balance (Pink/Magenta Gradient) */}
          <div className="shrink-0 w-[85vw] sm:w-[320px] md:w-[380px] relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#9a1b5e] via-[#7a134a] to-[#4a092b] p-6 shadow-2xl snap-center">
            {/* Abstract background shapes */}
            <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/3"></div>
            <div className="absolute bottom-0 right-0 w-32 h-32 bg-pink-500/20 rounded-full blur-xl translate-y-1/4 translate-x-1/4"></div>

            <div className="relative z-10 flex justify-between items-center mb-4">
              <p className="text-white/90 font-medium uppercase tracking-widest text-[10px] md:text-xs">Transaction Accounts (XX)</p>
              <button onClick={() => setShowBalance(!showBalance)} className="text-white/80 hover:text-white transition-colors p-1">
                {showBalance ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            <div className="relative z-10 mb-8 flex justify-between items-center">
              <div>
                <p className="text-white/70 text-xs md:text-sm mb-1">Combined Balance</p>
                <h2 className="text-3xl md:text-4xl font-bold text-white tracking-wide">
                  ₹{showBalance ? balance : 'XXXX.XX'}
                </h2>
              </div>
              <button className="text-white/80 hover:text-white transition-transform hover:rotate-180 p-2">
                <RefreshCcw size={20} />
              </button>
            </div>

            <div className="relative z-10 flex gap-6 md:gap-8 pt-2">
              <button 
  onClick={() => navigate('/account/view-account')} 
  className="text-white font-bold text-sm border-b-2 border-white pb-1 hover:text-gray-200 transition-colors"
>
  View Accounts
</button>
              <button className="text-white font-bold text-sm border-b-2 border-white pb-1 hover:text-gray-200 transition-colors">Transactions</button>
            </div>
          </div>

          {/* Card 2: Investments (Light Theme) */}
          <div className="shrink-0 w-[85vw] sm:w-[320px] md:w-[350px] relative overflow-hidden rounded-3xl bg-[#eef0f6] p-6 shadow-xl snap-center">
            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#d8dce8] rounded-full"></div>
            <div className="absolute top-10 -right-4 w-24 h-24 bg-[#e2e5f0] rounded-full"></div>
            
            <div className="relative z-10 h-full flex flex-col justify-between">
              <div>
                <h3 className="font-bold text-[#1e222d] text-xs uppercase tracking-widest mb-6">Investments</h3>
                <p className="text-[#3b4155] text-lg font-medium pr-10">Ready to start investing?</p>
              </div>
              <button className="mt-8 text-[#1e222d] font-bold text-sm w-max border-b-2 border-[#1e222d] pb-0.5 hover:text-blue-700 hover:border-blue-700 transition-colors">
                Invest Now
              </button>
            </div>
          </div>

          {/* Card 3: Personal Finance Manager (Light Theme) */}
          <div className="shrink-0 w-[85vw] sm:w-[320px] md:w-[350px] relative overflow-hidden rounded-3xl bg-[#eef0f6] p-6 shadow-xl snap-center">
             <div className="absolute -bottom-8 -right-8 w-48 h-48 bg-[#d8dce8] rounded-full"></div>
             <div className="absolute top-4 right-10 w-20 h-20 bg-[#e2e5f0] rounded-full"></div>
             
             <div className="relative z-10 h-full flex flex-col justify-between">
              <div>
                <h3 className="font-bold text-[#1e222d] text-xs uppercase tracking-widest mb-4">Personal Finance Manager</h3>
                <p className="text-[#3b4155] text-base leading-snug font-medium pr-6">One dashboard, all your finances-start using Personal Financial Manager now!</p>
              </div>
              <button className="mt-6 text-[#1e222d] font-bold text-sm w-max border-b-2 border-[#1e222d] pb-0.5 hover:text-blue-700 hover:border-blue-700 transition-colors">
                Start Now
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Payments & Transfers Section */}
      <div className="pt-4">
        <h3 className="text-lg font-bold text-white mb-4">Payments & Transfers</h3>
        
        <div className="flex border-b border-gray-800 mb-6 overflow-x-auto custom-scrollbar">
          <button className="px-4 py-2 border-b-2 border-blue-500 text-blue-500 font-bold whitespace-nowrap">UPI</button>
          <button className="px-4 py-2 text-gray-400 hover:text-gray-200 font-medium whitespace-nowrap">Fund Transfer</button>
          <button className="px-4 py-2 text-gray-400 hover:text-gray-200 font-medium whitespace-nowrap">Bills</button>
          <button className="px-4 py-2 text-gray-400 hover:text-gray-200 font-medium whitespace-nowrap">Yono Cash</button>
        </div>

        <div className="bg-[#131b2c] border border-gray-800 rounded-2xl p-6">
          <div className="bg-[#1e222d] border border-gray-700 rounded-xl p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
              </div>
              <p className="text-sm text-gray-300 font-medium">You haven't created a UPI ID yet.</p>
            </div>
            <button className="text-blue-400 font-bold text-sm hover:text-blue-300 transition-colors shrink-0">
              Activate UPI ID
            </button>
          </div>

          <div className="flex justify-between items-center mb-6">
            <h4 className="text-gray-400 font-medium">UPI Payments</h4>
            <button className="text-blue-500 font-bold text-sm">View All</button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button className="flex flex-col items-center justify-center gap-3 p-4 bg-[#0b1221] border border-gray-800 rounded-xl hover:border-blue-500 transition-colors group">
               <Wallet className="text-gray-400 group-hover:text-blue-500 transition-colors" />
               <span className="text-xs text-gray-400 group-hover:text-gray-200 font-medium">To UPI ID</span>
            </button>
            <button className="flex flex-col items-center justify-center gap-3 p-4 bg-[#0b1221] border border-gray-800 rounded-xl hover:border-blue-500 transition-colors group">
               <CreditCard className="text-gray-400 group-hover:text-blue-500 transition-colors" />
               <span className="text-xs text-gray-400 group-hover:text-gray-200 font-medium">To Bank A/c</span>
            </button>
            <button className="flex flex-col items-center justify-center gap-3 p-4 bg-[#0b1221] border border-gray-800 rounded-xl hover:border-blue-500 transition-colors group">
               <User className="text-gray-400 group-hover:text-blue-500 transition-colors" />
               <span className="text-xs text-gray-400 group-hover:text-gray-200 font-medium">To Contacts</span>
            </button>
            <button className="flex flex-col items-center justify-center gap-3 p-4 bg-[#0b1221] border border-gray-800 rounded-xl hover:border-blue-500 transition-colors group">
               <Receipt className="text-gray-400 group-hover:text-blue-500 transition-colors" />
               <span className="text-xs text-gray-400 group-hover:text-gray-200 font-medium">History</span>
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default AccountDashboard;